import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { defaultLandingForRole } from "@/context/AuthContext";

export default function ChangePassword() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("Password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      updateUser({ must_change_password: false });
      toast.success("Password changed");
      navigate(defaultLandingForRole(user.role), { replace: true });
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5] p-6">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-8" data-testid="change-password-page">
        <div className="flex items-center gap-2 mb-6">
          <span className="h-9 w-9 grid place-items-center bg-[#F97316] rounded-sm">
            <KeyRound className="h-5 w-5 text-black" strokeWidth={2.5} />
          </span>
          <span className="font-heading font-black tracking-tight">SECURE YOUR ACCOUNT</span>
        </div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">
          One-time action
        </div>
        <h2 className="font-heading text-2xl font-black tracking-tight mb-2">Set a new password</h2>
        <p className="text-xs text-zinc-500 mb-6">
          You are signed in with a temporary password. Please set a new one to continue.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Current (temporary) password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required className="rounded-sm h-11" data-testid="cp-current" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} className="rounded-sm h-11" data-testid="cp-new" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} className="rounded-sm h-11" data-testid="cp-confirm" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="cp-submit" className="btn-pressable w-full h-11 rounded-sm bg-[#F97316] hover:bg-[#EA580C] text-white font-mono uppercase tracking-widest text-xs">
            {submitting ? "Updating…" : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
